import { usePlayer } from '@audio-network/player'
import clsx from 'clsx'
import { memo } from 'react'

import TrackActions from '@/components/shared/TrackActions/TrackActions'
import { useReorder } from '@/pages/project/edit/reorder.hook'
import { isModalOpen } from '@/utils/isModalOpen'

import TrackArtwork from './TrackArtwork/TrackArtwork'
import TrackDetails from './TrackDetails/TrackDetails'
import TrackDurationAndTempo from './TrackDurationAndTempo/TrackDurationAndTempo'
import TrackPlayback from './TrackPlayback/TrackPlayback'
import styles from './TrackRow.module.css'
import { TrackRowProps } from './TrackRow.types'
import { buildDragTrackData } from './TrackRow.utils'
import TrackWaveform from './TrackWaveform/TrackWaveform'

const TrackRow = memo(
	({ dragImageRef, track, source }: TrackRowProps): JSX.Element => {
		const { playHistory } = usePlayer()
		const {
			handleDragOver,
			handleDragEnter,
			handleDragLeave,
			handleDragEnd,
			handleDrop,
		} = useReorder(track)

		const handleDragStart = (event: React.DragEvent<HTMLElement>): void => {
			if (isModalOpen()) {
				return
			}

			event.dataTransfer.clearData()

			const dragData = buildDragTrackData(track, source)
			event.dataTransfer.setData('text/plain', JSON.stringify(dragData))

			event.dataTransfer.effectAllowed = 'move'

			const dragImage = dragImageRef.current

			if (!dragImage) {
				return
			}

			dragImage.style.transform = 'translate(0, 0)'
			dragImage.innerHTML = `
			${track.title}
		`

			event.dataTransfer.setDragImage(dragImage, 0, 0)
		}

		const played = playHistory.includes(track.id)

		return (
			<article
				className={clsx(styles.trackRow, played && styles.played)}
				draggable
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragEnd={handleDragEnd}
				onDrop={handleDrop}
				data-trackrow-id={track.id}
				data-testid="track-row"
			>
				<TrackArtwork track={track} />
				<TrackPlayback track={track} source={source} />
				<TrackDetails track={track} source={source} />
				<TrackWaveform track={track} />
				<TrackDurationAndTempo track={track} />
				<TrackActions track={track} source={source} location="Track Row" />
			</article>
		)
	}
)

TrackRow.displayName = 'TrackRow'

export default TrackRow
